import type React from "react";

import type { WellboreHeader_api } from "@api";
import type { SelectOption } from "@lib/components/Select";
import { Select } from "@lib/components/Select";

import type {
    CustomSettingImplementation,
    SettingComponentProps,
} from "../../interfacesAndTypes/customSettingImplementation";
import { assertStringArrayOrNull } from "../utils/structureValidation";

import {
    fixupValue,
    isValueValid,
    makeValueConstraintsIntersectionReducerDefinition,
} from "./_shared/arrayMultiSelect";

type InternalValueType = string[] | null;
type ExternalValueType = WellboreHeader_api[] | null;
type ValueConstraintsType = WellboreHeader_api[];

export class DrilledWellboresSetting implements CustomSettingImplementation<
    InternalValueType,
    ExternalValueType,
    ValueConstraintsType
> {
    defaultValue: InternalValueType = null;
    valueConstraintsIntersectionReducerDefinition =
        makeValueConstraintsIntersectionReducerDefinition<ValueConstraintsType>(
            (a, b) => a.wellboreUuid === b.wellboreUuid,
        );

    mapInternalToExternalValue(
        internalValue: InternalValueType,
        valueConstraints: ValueConstraintsType,
    ): ExternalValueType {
        if (internalValue === null) {
            return null;
        }

        const externalValues = valueConstraints.filter((wellbore) => internalValue.includes(wellbore.wellboreUuid));
        return externalValues;
    }

    serializeValue(value: InternalValueType): string {
        return JSON.stringify(value);
    }

    deserializeValue(serializedValue: string): InternalValueType {
        const parsed = JSON.parse(serializedValue);
        assertStringArrayOrNull(parsed);
        return parsed;
    }

    fixupValue(currentValue: InternalValueType, valueConstraints: ValueConstraintsType): InternalValueType {
        const fixedValue = fixupValue<string, WellboreHeader_api>(
            currentValue,
            valueConstraints,
            mappingFunc,
            "allAvailable",
        );

        if (fixedValue.length === 0) {
            return valueConstraints.map(mappingFunc);
        }

        return fixedValue;
    }

    isValueValid(currentValue: InternalValueType, valueConstraints: ValueConstraintsType): boolean {
        return isValueValid<string, WellboreHeader_api>(currentValue, valueConstraints, mappingFunc);
    }

    makeComponent(): (props: SettingComponentProps<InternalValueType, ValueConstraintsType>) => React.ReactNode {
        return function DrilledWellbores(props: SettingComponentProps<InternalValueType, ValueConstraintsType>) {
            const valueConstraints = props.valueConstraints ?? [];

            const options: SelectOption[] = valueConstraints?.map((ident) => ({
                value: ident.wellboreUuid,
                label: ident.uniqueWellboreIdentifier,
            }));

            function handleChange(selectedUuids: string[]) {
                props.onValueChange(selectedUuids);
            }

            return (
                <div className="mt-4xs flex flex-col gap-1">
                    <Select
                        filter
                        options={options}
                        value={props.value ?? []}
                        onValueChange={handleChange}
                        showQuickSelectButtons={true}
                        disabled={props.disabled}
                        multiple={true}
                        size={5}
                    />
                </div>
            );
        };
    }
}

function mappingFunc(value: WellboreHeader_api): string {
    return value.wellboreUuid;
}
